import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, FileText, Check, Download, Eye, Mail } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { DocumentUploadButton } from '@/components/documents/DocumentUploadButton';
import { supabase } from '@/integrations/supabase/client';
import { DocumentPreviewModal } from '@/components/documents/DocumentPreviewModal';
import { FinalizeWithSignatures } from '@/components/documents/FinalizeWithSignatures';
import DocumentEmailWizard from '@/components/documents/DocumentEmailWizard';
import { embedSignaturesIntoHtml } from '@/lib/signatureEmbedding';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DocumentSigningControl } from '../DocumentSigningControl';
import { SealApplyCheckbox } from '../SealApplyCheckbox';
import { useAssociationSeal } from '../useAssociationSeal';
import { generatedFilePath } from '../docProvenance';
import { finishLegacyHtml } from '../legacyDocumentFinish';
import { legacySigningMeta } from '../legacySigningSpecs';
import { downloadDocumentById } from '@/lib/downloadDocumentById';

interface OfficerPhase1Props {
  task: any;
  phaseData: any;
  onPhaseComplete: () => void;
}

export function OfficerPhase1({ task, phaseData, onPhaseComplete }: OfficerPhase1Props) {
  const { toast } = useToast();
  const [documents, setDocuments] = useState<any[]>([]);
  const seal = useAssociationSeal(task.una_id);
  const [generating, setGenerating] = useState<string | null>(null);
  const [previewDocument, setPreviewDocument] = useState<any | null>(null);
  const [isCompleting, setIsCompleting] = useState<boolean>(false);
  const [unaData, setUnaData] = useState<any | null>(null);
  const [signatureWizardOpen, setSignatureWizardOpen] = useState(false);
  const [selectedDocForSignature, setSelectedDocForSignature] = useState<any | null>(null);
  const [emailWizardOpen, setEmailWizardOpen] = useState(false);
  const [selectedDocForEmail, setSelectedDocForEmail] = useState<string | null>(null);
  const [signatureRequests, setSignatureRequests] = useState<Record<string, any[]>>({});

  const workflowData = task.workflow_data || {};

  // Required documents for Phase 1
  const requiredDocuments = [
    { 
      key: 'form_officer_information', 
      title: 'Officer Information Collection Form',
      requiresSignature: false
    },
    { 
      key: 'resolution_officer_appointment', 
      title: 'Officer Appointment Resolution',
      requiresSignature: true
    },
  ];

  useEffect(() => {
    loadDocuments();
    loadUnaData();
  }, [task.id]);

  useEffect(() => {
    if (documents.length > 0) {
      loadSignatureRequests();
    }
  }, [documents]);

  async function loadSignatureRequests() {
    const docIds = documents.map(d => d.id);
    if (docIds.length === 0) return;

    const { data, error } = await supabase
      .from('document_signers')
      .select('*, signature_requests!inner(document_id)')
      .in('signature_requests.document_id', docIds);

    if (error) {
      console.error('Error loading signature requests:', error);
      return;
    }

    // Group by document_id
    const grouped: Record<string, any[]> = {};
    for (const signer of data || []) {
      const docId = signer.signature_requests?.document_id;
      if (docId) {
        if (!grouped[docId]) grouped[docId] = [];
        grouped[docId].push(signer);
      }
    }
    setSignatureRequests(grouped);
  }

  async function loadUnaData() {
    if (!task.una_id) return;
    
    const { data, error } = await supabase
      .from('unas')
      .select(`
        *,
        una_members(
          id,
          user_id,
          full_name,
          email,
          role,
          status
        )
      `)
      .eq('id', task.una_id)
      .maybeSingle();

    if (!error && data) {
      // Fetch Secretary Treasurer profile from profiles table using treasury_secretary_id
      let treasurySecretaryName = null;
      let treasurySecretaryEmail = null;
      
      if (data.treasury_secretary_id) {
        const { data: tsProfile } = await supabase
          .from('profiles')
          .select('id, full_name, email')
          .eq('id', data.treasury_secretary_id)
          .maybeSingle();
        
        if (tsProfile) {
          treasurySecretaryName = tsProfile.full_name;
          treasurySecretaryEmail = tsProfile.email;
        }
      }
      
      // Extend the data object with treasury secretary info
      setUnaData({
        ...data,
        treasury_secretary_name: treasurySecretaryName,
        treasury_secretary_email: treasurySecretaryEmail
      });
    }
  }

  async function loadDocuments() {
    const { data, error } = await supabase
      .from('generated_documents')
      .select('*')
      .eq('task_id', task.id)
      .order('generated_at', { ascending: true }) as { data: any[] | null; error: any };

    if (error) {
      console.error('Error loading documents:', error);
      return;
    }

    // Filter to phase 1 documents
    const phase1Docs = (data || []).filter(d => {
      const templateKey = d.merge_field_data?.template_key || '';
      return templateKey && (
        templateKey.includes('form_officer_information') ||
        templateKey.includes('resolution_officer_appointment')
      );
    });

    setDocuments(phase1Docs);
  }

  async function handleGenerateDocument(docReq: any) {
    setGenerating(docReq.key);
    
    try {
      // Generate basic HTML content for officer documents
      const htmlContent = finishLegacyHtml(
        generateOfficerDocumentHtml(docReq.key, workflowData, unaData),
        { entityName: unaData?.name, sealEnabled: seal.sealEnabled, sealSvg: seal.sealSvg },
      );

      if (!htmlContent) {
        throw new Error('Failed to generate document content');
      }

      // Call edge function to generate PDF
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-pdf-server`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ html: htmlContent, author: unaData?.name || undefined }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'PDF generation failed');
      }

      const { pdf: pdfBase64, size } = await response.json();

      // Convert base64 to Blob
      const byteCharacters = atob(pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const pdfBlob = new Blob([byteArray], { type: 'application/pdf' });

      // Upload to storage
      const filePath = generatedFilePath(unaData?.name, docReq.key, task.id);

      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, pdfBlob, {
          contentType: 'application/pdf',
          upsert: true,
        });

      if (uploadError) throw uploadError;

      // Buckets are private — store path as reference
      const { data: { session: authSession } } = await supabase.auth.getSession();
      const createdBy = task.user_id || authSession?.user?.id || null;

      // Store document record
      const documentRecord = {
        task_id: task.id,
        una_id: task.una_id,
        document_name: docReq.title,
        document_type: 'workflow',
        file_url: filePath,
        file_path: filePath,
        file_size: size,
        status: docReq.requiresSignature ? 'draft' : 'final',
        custom_content: htmlContent,
        created_by: createdBy,
        merge_field_data: { 
          phase: 'phase_1',
          template_key: docReq.key,
          ...legacySigningMeta(docReq.key, seal.sealEnabled),
          workflow_data: workflowData 
        },
      };

      const { data: insertedDoc, error: insertError } = await supabase
        .from('generated_documents')
        .insert(documentRecord)
        .select()
        .single();

      if (insertError) {
        console.error('Database insert error:', insertError);
        throw insertError;
      }

      toast({
        title: 'Document Generated',
        description: `${docReq.title} has been created successfully.`,
      });

      await loadDocuments();
    } catch (error: any) {
      console.error('Error generating document:', error);
      toast({
        title: 'Generation Failed',
        description: error.message || 'Failed to generate document',
        variant: 'destructive',
      });
    } finally {
      setGenerating(null);
    }
  }

  async function handleCollectSignature(doc: any) {
    setSelectedDocForSignature(doc);
    setSignatureWizardOpen(true);
  }

  async function handleMarkPhaseComplete() {
    // Check at least one document generated
    if (documents.length < 1) {
      toast({
        title: 'Documents Required',
        description: 'Please generate at least one document before completing this phase.',
        variant: 'destructive',
      });
      return;
    }

    toast({
      title: 'Phase 1 Complete',
      description: 'Officer appointment documents prepared. Proceeding to Phase 2.',
    });

    onPhaseComplete();
  }

  function getDocumentStatus(docKey: string) {
    const doc = documents.find(d => d.merge_field_data?.template_key === docKey);
    if (!doc) return { status: 'not_generated', doc: null };
    
    const req = requiredDocuments.find(r => r.key === docKey);
    
    if (doc.status === 'finalized') {
      // Check if there are pending member signatures
      const signers = signatureRequests[doc.id] || [];
      const pendingMemberSigs = signers.filter(s => 
        s.status === 'pending' && 
        s.signer_role !== 'treasury_secretary'
      );
      
      if (pendingMemberSigs.length > 0) {
        return { status: 'awaiting_member_signatures', doc };
      }
      return { status: 'complete', doc };
    }
    
    if (req?.requiresSignature && doc.status === 'draft') {
      return { status: 'needs_signature', doc };
    }
    
    return { status: 'complete', doc };
  }

  return (
    <div className="space-y-6">
      {/* Phase Header */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <CardTitle>Phase 1: Prepare Appointment Documents</CardTitle>
                <Badge>Current Phase</Badge>
              </div>
              <p className="text-muted-foreground">
                Create officer appointment resolution and information forms
              </p>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Steps to Complete */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Steps to Complete:</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm">
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-muted-foreground mt-0.5" />
              <span>Collect officer information and position details</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-muted-foreground mt-0.5" />
              <span>Generate officer information collection form</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-muted-foreground mt-0.5" />
              <span>Draft officer appointment resolution</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-muted-foreground mt-0.5" />
              <span>Obtain board approval signatures</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Required Documents */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Required Documents:</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <SealApplyCheckbox
              available={seal.sealAvailable}
              checked={seal.applySeal}
              onChange={seal.setApplySeal}
            />
            {requiredDocuments.map((docReq) => {
              const { status, doc } = getDocumentStatus(docReq.key);

              return (
                <div key={docReq.key} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <div className="font-medium">{docReq.title}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Status Badge */}
                    {doc && (
                      <Badge variant={status === 'complete' ? 'default' : status === 'awaiting_member_signatures' ? 'outline' : 'secondary'}>
                        {status === 'complete' ? 'Signed & Complete' :
                         status === 'awaiting_member_signatures' ? 'Awaiting Member Signatures' :
                         status === 'needs_signature' ? 'Needs Signature' : 
                         'Generated'}
                      </Badge>
                    )}
                    
                    {!doc ? (
                      <Button
                        onClick={() => handleGenerateDocument(docReq)}
                        disabled={generating === docReq.key}
                        size="sm"
                      >
                        {generating === docReq.key ? 'Generating...' : 'Generate'}
                      </Button>
                    ) : (
                      <>
                        {doc.status === 'draft' && docReq.requiresSignature && (
                          <Button onClick={() => handleCollectSignature(doc)} size="sm">
                            Sign & Finalize
                          </Button>
                        )}
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPreviewDocument(doc)}
                        >
                          <Eye className="h-3 w-3" />
                          Preview
                        </Button>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              await downloadDocumentById(doc.id, { underscoreFilename: true });
                            } catch (error) {
                              console.error('Download error:', error);
                            }
                          }}
                        >
                          <Download className="h-3 w-3" />
                          Download
                        </Button>
                        
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedDocForEmail(doc.id);
                            setEmailWizardOpen(true);
                          }}
                        >
                          <Mail className="h-3 w-3" />
                          Email
                        </Button>
                        
                        {doc.merge_field_data?.requires_signature !== false && task.una_id && (
                          <div className="w-full">
                            <DocumentSigningControl
                              doc={doc}
                              unaId={task.una_id}
                              sealEnabled={seal.sealAvailable}
                              onChanged={loadDocuments}
                            />
                          </div>
                        )}
                        
                        <DocumentUploadButton
                          documentId={doc.id}
                          documentName={doc.document_name}
                          onUploadComplete={() => loadDocuments()}
                          fileUrl={doc.file_url}
                          hasUploadedFile={doc.file_path?.includes('-uploaded.pdf')}
                        />
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Complete Phase Button - Always show, but disable when not ready */}
      <Alert className={documents.length < 1 ? 'border-muted' : 'border-green-200 bg-green-50'}>
        <AlertDescription>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">
                {documents.length >= 1 ? 'Documents Ready' : 'Complete Phase 1'}
              </p>
              <p className="text-sm text-muted-foreground">
                {documents.length >= 1 
                  ? 'At least one document prepared. Ready to proceed.'
                  : 'Generate at least one document to continue.'}
              </p>
            </div>
            <Button 
              onClick={handleMarkPhaseComplete}
              disabled={documents.length < 1}
            >
              Complete Phase 1
            </Button>
          </div>
        </AlertDescription>
      </Alert>

      {/* Modals */}
      {previewDocument && (
        <DocumentPreviewModal
          document={previewDocument}
          open={!!previewDocument}
          onClose={() => setPreviewDocument(null)}
        />
      )}

      {selectedDocForSignature && (
        <FinalizeWithSignatures
          documentTitle={`Finalize ${selectedDocForSignature?.document_name || 'Document'}`}
          treasurySecretaryName={unaData?.treasury_secretary_name || 'Secretary Treasurer'}
          primaryOfficerRoleLabel={unaData?.primary_officer_role || 'Secretary Treasurer'}
          requiredSigners={['treasury_secretary']}
          onFinalize={async (signatures) => {
            try {
              const { data: docData, error: fetchError } = await supabase
                .from('generated_documents')
                .select('custom_content, document_name, id')
                .eq('id', selectedDocForSignature.id)
                .single();
              
              if (fetchError) throw fetchError;
              if (!docData?.custom_content) {
                throw new Error('Document HTML not found in custom_content');
              }
              
              const { embedSignaturesIntoHtml } = await import('@/lib/signatureEmbedding');
              const finalizedTimestamp = new Date().toISOString();
              
              const signatureArray = [];
              if (signatures.treasury_secretary_signature) {
                signatureArray.push({
                  role: 'treasury_secretary',
                  signer_id: unaData.treasury_secretary_id || '',
                  signature_data: signatures.treasury_secretary_signature,
                  signed_at: finalizedTimestamp
                });
              }
              
              const signedHtml = await embedSignaturesIntoHtml(
                docData.custom_content,
                signatureArray,
                finalizedTimestamp
              );
              
              const { data: { session } } = await supabase.auth.getSession();
              if (!session) throw new Error('No authentication session');
              
              const pdfResponse = await fetch(
                `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-pdf-server`,
                {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`,
                  },
                  body: JSON.stringify({ html: signedHtml, author: unaData?.name || undefined }),
                }
              );
              
              if (!pdfResponse.ok) {
                const errorData = await pdfResponse.json();
                throw new Error(errorData.error || 'PDF generation failed');
              }
              
              const { pdf: pdfBase64, size } = await pdfResponse.json();
              
              const byteCharacters = atob(pdfBase64);
              const byteNumbers = new Array(byteCharacters.length);
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
              }
              const pdfBlob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
              
              const signedFilePath = generatedFilePath(unaData?.name, `${docData.document_name}_signed`, docData.id);
              
              const { error: uploadError } = await supabase.storage
                .from('documents')
                .upload(signedFilePath, pdfBlob, {
                  contentType: 'application/pdf',
                  upsert: true,
                });
              
              if (uploadError) throw uploadError;
              
              // Buckets are private — store path as reference
              const { error } = await supabase
                .from('generated_documents')
                .update({
                  status: 'finalized',
                  finalized_at: new Date().toISOString(),
                  finalized_by: task.user_id,
                  file_url: signedFilePath,
                  file_path: signedFilePath,
                  file_size: size,
                  custom_content: signedHtml,
                  signature_history: {
                    finalized_signatures: {
                      treasury_secretary_signature: signatures.treasury_secretary_signature,
                      treasury_secretary_date: signatures.treasury_secretary_signature_date,
                      finalized_at: new Date().toISOString(),
                    }
                  }
                })
                .eq('id', selectedDocForSignature.id);
              
              if (error) throw error;
              
              setSignatureWizardOpen(false);
              setSelectedDocForSignature(null);
              
              await new Promise(resolve => setTimeout(resolve, 500));
              await loadDocuments();
              
              toast({
                title: 'Document Signed & Finalized',
                description: 'Signatures have been embedded in the PDF',
              });
            } catch (error: any) {
              console.error('Signature error:', error);
              toast({
                title: 'Signature Failed',
                description: error.message || 'Could not save signatures',
                variant: 'destructive',
              });
            }
          }}
          onCancel={() => {
            setSignatureWizardOpen(false);
            setSelectedDocForSignature(null);
          }}
        />
      )}

      {selectedDocForEmail && (
        <DocumentEmailWizard
          open={emailWizardOpen}
          onOpenChange={setEmailWizardOpen}
          taskId={task.id}
          unaId={task.una_id}
          preSelectedDocumentId={selectedDocForEmail}
          onSuccess={() => {
            setEmailWizardOpen(false);
            setSelectedDocForEmail(null);
            toast({
              title: 'Email Sent',
              description: 'Document emailed successfully',
            });
          }}
        />
      )}
    </div>
  );
}

// Helper function to generate officer document HTML
function generateOfficerDocumentHtml(docKey: string, workflowData: any, unaData: any): string {
  // Format dates consistently
  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const professionalStyles = `
    <style>
      body {
        font-family: 'Cambria', 'Georgia', serif;
        font-size: 12pt;
        line-height: 1.8;
        max-width: 8.5in;
        margin: 0 auto;
        padding: 1in;
      }
      h1 {
        text-align: center;
        font-size: 16pt;
        margin-bottom: 0.5in;
        font-weight: bold;
        text-transform: uppercase;
      }
      h2 {
        font-size: 14pt;
        margin-top: 0.3in;
        margin-bottom: 0.2in;
      }
      h3 {
        font-size: 12pt;
        margin-top: 0.4in;
        margin-bottom: 0.3in;
        font-weight: 600;
        border-bottom: 2px solid #333;
        padding-bottom: 0.15in;
      }
      .whereas, .resolved {
        margin-bottom: 0.25in;
        text-align: justify;
      }
      .whereas::before {
        content: "WHEREAS, ";
        font-weight: bold;
      }
      .signature-section {
        margin-top: 0.75in;
        page-break-inside: avoid;
      }
      .signature-block {
        margin-top: 0.5in;
        margin-bottom: 0.4in;
        padding: 0.3in;
        border: 1px solid #ddd;
        border-radius: 4px;
        background-color: #fafafa;
        page-break-inside: avoid;
      }
      .signature-line {
        border-bottom: 2px solid #333;
        min-height: 0.6in;
        width: 3.5in;
        margin-bottom: 0.15in;
        padding: 0.1in;
      }
      .signature-label {
        font-size: 10pt;
        margin-top: 0.1in;
        font-weight: 600;
      }
      .date-line {
        border-bottom: 1px solid #666;
        width: 2in;
        display: inline-block;
        margin-left: 0.15in;
      }
    </style>
  `;

  if (docKey === 'form_officer_information') {
    return `
      <!DOCTYPE html>
      <html><head>${professionalStyles}</head><body>
        <h1>Officer Information Collection Form</h1>
        <p style="text-align: center; font-size: 11pt; margin-bottom: 0.4in; color: #666;">For UNA Records</p>
        
        <h3>Officer Personal Information</h3>
        <p><strong>Full Legal Name:</strong> ${workflowData.officer_full_name || ''}</p>
        <p><strong>Officer Position:</strong> ${workflowData.officer_position || ''}</p>
        <p><strong>Email Address:</strong> ${workflowData.officer_email || 'N/A'}</p>
        <p><strong>Phone Number:</strong> ${workflowData.officer_phone || 'N/A'}</p>
        
        <h3>Residential Address</h3>
        <p><strong>Street Address:</strong> ${workflowData.officer_address || ''}</p>
        <p><strong>City:</strong> ${workflowData.officer_city || ''}</p>
        <p><strong>State:</strong> ${workflowData.officer_state || ''}</p>
        <p><strong>ZIP Code:</strong> ${workflowData.officer_zip || ''}</p>
        
        ${workflowData.officer_duties ? `<h3>Duties & Responsibilities</h3><p>${workflowData.officer_duties}</p>` : ''}
        
        <p style="margin-top: 0.6in; font-size: 10pt; color: #666; text-align: center;">This form is for internal UNA records and documentation purposes.</p>
      </body></html>
    `;
  }

  if (docKey === 'resolution_officer_appointment') {
    // Get board members from UNA data - normalize roles for comparison
    const boardMembers = (unaData?.una_members || []).filter((m: any) => {
      const role = (m.role || '').toLowerCase().trim().replace(/\s+/g, '_');
      return m.status === 'active' && (
        role === 'board_member' || 
        role.includes('board') ||
        role === 'member'
      );
    });
    
    // Secretary Treasurer is in unas.treasury_secretary_id, NOT una_members
    const treasurySecretaryId = unaData?.treasury_secretary_id;
    const treasurySecretaryName = unaData?.treasury_secretary_name || 'Secretary Treasurer';
    
    // Generate signature blocks for all board members
    // CRITICAL: Use member.id (una_members.id) which is ALWAYS populated, not user_id which can be null
    // Also ensure data-signer-id is on the INNER signature-line div for proper embedding
    const boardMemberSignatureBlocks = boardMembers.map((member: any) => `
      <div class="signature-block">
        <p><strong>Board Member Approval:</strong></p>
        <div class="signature-line" data-signer-id="${member.id}" data-signature-role="board_member"></div>
        <div class="signature-label">${member.full_name}, Board Member</div>
        <div style="margin-top: 0.15in;">Date: <span class="date-line"></span></div>
      </div>
    `).join('');
    
    // Secretary Treasurer signature block - data-signer-id on INNER signature-line div, NOT outer signature-block
    const treasurySecretaryBlock = treasurySecretaryId ? `
      <h3>Board Secretary Attestation</h3>
      <p style="margin-bottom: 0.3in; font-style: italic; font-size: 11pt;">
        As Board Secretary, I attest that this resolution was duly adopted:
      </p>
      <div class="signature-block">
        <div class="signature-line" data-signer-id="${treasurySecretaryId}" data-signature-role="primary_officer"></div>
        <div class="signature-label">${treasurySecretaryName}, ${unaData?.primary_officer_role || 'Board Secretary'}</div>
        <div style="margin-top: 0.15in;">Date: <span class="date-line"></span></div>
      </div>
    ` : '';
    
    return `
      <!DOCTYPE html>
      <html><head>${professionalStyles}</head><body>
        <h1>RESOLUTION OF APPOINTMENT OF OFFICER</h1>
        <h2>${unaData?.name || workflowData.una_name || ''}</h2>
        <p><strong>Date:</strong> ${formatDate(workflowData.board_approval_date)}</p>
        
        <div class="whereas">the Board of ${unaData?.name || ''} has determined it appropriate to appoint an officer to serve in the capacity of ${workflowData.officer_position || ''};</div>
        
        <div class="whereas">${workflowData.officer_full_name || ''} has been identified as qualified and willing to serve in this capacity;</div>
        
        <p style="margin-top: 0.3in; font-weight: bold;">NOW, THEREFORE, BE IT RESOLVED that:</p>
        <ol>
          <li>${workflowData.officer_full_name || ''} is hereby appointed as ${workflowData.officer_position || ''} of ${unaData?.name || ''}, effective ${formatDate(workflowData.effective_date)}.</li>
          <li>The officer shall serve until resignation, removal, or replacement by the Board.</li>
          <li>This resolution is effective immediately upon adoption.</li>
        </ol>
        
        <div class="signature-section">
          <h3>Board Member Approval</h3>
          <p style="margin-bottom: 0.3in; font-style: italic; font-size: 11pt;">
            The undersigned Board Members hereby approve this resolution:
          </p>
          ${boardMemberSignatureBlocks}
          
          ${treasurySecretaryBlock}
        </div>
      </body></html>
    `;
  }

  return '';
}
